import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import {
  Upload,
  Trash2,
  X,
  FileText,
  File,
  Loader2,
  CheckCircle,
  AlertTriangle,
  PlayCircle,
} from "lucide-react";
import settingsService from "../../../services/settings.service";
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import NodeDocsButton from "../../docs/NodeDocsButton";

// Interfaces remain the same
interface FileData {
  name: string;
  size: number;
  type: string;
  preview?: string;
  uploading?: boolean;
  uploaded?: boolean;
  error?: string;
  uploadData?: UploadResponse;
}

interface UploadResponse {
  name: string;
  key: string;
  url: string;
  fileType: string;
  fileSize: number;
  filePages: number;
}

export const DocumentUploadNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  // --- STATE AND HOOKS ---
  const initializeFiles = (): FileData[] => {
    if (!data || Object.keys(data).length === 0) return [];

    // Handle both camelCase and snake_case from API
    const fileName = data.fileName || data.file_name;
    const fileKey = data.key;
    const fileUrl = data.url;
    const fileType = data.fileType || data.file_type || "";
    const fileSize = data.fileSize || data.file_size || 0;
    const filePages = data.filePages || data.file_pages || 0;

    // Check if we have the required fields (fileName, key, url)
    if (fileName && fileKey && fileUrl) {
      return [
        {
          name: fileName,
          size: fileSize,
          type: fileType,
          uploaded: true,
          uploadData: {
            name: fileName,
            key: fileKey,
            url: fileUrl,
            fileType: fileType,
            fileSize: fileSize,
            filePages: filePages,
          },
        },
      ];
    }
    return [];
  };

  const [files, setFiles] = useState<FileData[]>(initializeFiles());
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isClearing = useRef(false);
  const { setNodes, deleteElements, getNodes, getEdges } = useReactFlow();

  // Get execution status from node data
  const executionStatus = data?.executionStatus || null; // 'running', 'success', 'error'
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  // Validation status
  const currentNode = useMemo(() => {
    return getNodes().find((n) => n.id === id);
  }, [getNodes, id, data]);

  const validationStatus = useMemo(() => {
    if (!currentNode) return { isValid: true, errors: [] };
    return getNodeValidationStatus(currentNode, getEdges(), getNodes());
  }, [currentNode, getEdges, getNodes]);

  // --- HANDLERS ---
  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => e.stopPropagation(),
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  const processFiles = useCallback((fileList: FileList) => {
    // Allow only one file
    const file = fileList[0];
    if (!file) return;

    const fileData: FileData = {
      name: file.name,
      size: file.size,
      type: file.type,
      uploading: true,
    };
    setFiles([fileData]); // Replace existing files

    settingsService
      .uploadFileWithDetails(file)
      .then((uploadResponse) => {
        setFiles((prev) =>
          prev.map((f) =>
            f.name === file.name
              ? {
                  ...f,
                  uploading: false,
                  uploaded: true,
                  uploadData: uploadResponse,
                }
              : f,
          ),
        );
      })
      .catch((error) => {
        console.error("Upload failed:", error);
        setFiles((prev) =>
          prev.map((f) =>
            f.name === file.name
              ? { ...f, uploading: false, error: "Upload failed" }
              : f,
          ),
        );
      });
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      // Reset input value so same file can be selected again
      e.target.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (files.length === 0) setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (files.length === 0 && e.dataTransfer.files) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleClearFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Set flag to prevent useEffect from repopulating
    isClearing.current = true;
    setFiles([]);
    // Reset file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // Also clear the data from the node state but PRESERVE other data
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          const {
            fileName,
            file_name,
            key,
            url,
            fileType,
            file_type,
            fileSize,
            file_size,
            filePages,
            file_pages,
            ...rest
          } = node.data;
          return { ...node, data: rest };
        }
        return node;
      }),
    );
    // Reset flag after a short delay
    setTimeout(() => {
      isClearing.current = false;
    }, 100);
  };

  const handleBrowseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  // --- EFFECTS AND MEMOS ---
  // Re-initialize files when data changes externally (e.g., from API load or execution)
  useEffect(() => {
    // Skip if we're intentionally clearing
    if (isClearing.current) return;

    const fileName = data?.fileName || data?.file_name;
    const fileKey = data?.key;
    const fileUrl = data?.url;
    const fileType = data?.fileType || data?.file_type || "";
    const fileSize = data?.fileSize || data?.file_size || 0;
    const filePages = data?.filePages || data?.file_pages || 0;

    // Only re-initialize if we have file data and current files state is empty
    if (fileName && fileKey && fileUrl && files.length === 0) {
      setFiles([
        {
          name: fileName,
          size: fileSize,
          type: fileType,
          uploaded: true,
          uploadData: {
            name: fileName,
            key: fileKey,
            url: fileUrl,
            fileType: fileType,
            fileSize: fileSize,
            filePages: filePages,
          },
        },
      ]);
    }
  }, [
    data?.fileName,
    data?.file_name,
    data?.key,
    data?.url,
    data?.fileType,
    data?.file_type,
    data?.fileSize,
    data?.file_size,
    data?.filePages,
    data?.file_pages,
    files.length,
  ]);

  useEffect(() => {
    const uploadedFile = files.find((f) => f.uploaded && f.uploadData);
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          // Debug log to see what we are preserving
          // console.log(`🔄 Updating node ${id} data. Has onExecuteNode:`, !!node.data.onExecuteNode);

          return {
            ...node,
            data: uploadedFile
              ? {
                  ...node.data,
                  name: node.data.name || "FileUploadNode", // Preserve factory name
                  fileName: uploadedFile.uploadData.name, // Store file name separately
                  key: uploadedFile.uploadData.key,
                  url: uploadedFile.uploadData.url,
                  file_type: uploadedFile.uploadData.fileType,
                  file_size: uploadedFile.uploadData.fileSize,
                  file_pages: uploadedFile.uploadData.filePages || 0,
                }
              : { ...node.data, name: node.data.name || "FileUploadNode" }, // Preserve name even when empty
          };
        }
        return node;
      }),
    );
  }, [files, id, setNodes]);

  const statusConfig = useMemo(() => {
    // Execution status takes priority
    if (isExecuting) {
      return { bg: "bg-blue-500", text: "Executing...", pulse: true };
    } else if (executionSuccess) {
      return {
        bg: "bg-green-500",
        text: "Executed Successfully",
        pulse: false,
      };
    } else if (executionError) {
      return { bg: "bg-red-500", text: "Execution Failed", pulse: false };
    }

    // Upload status
    if (files.length > 0) {
      const file = files[0];
      if (file.uploading)
        return { bg: "bg-blue-500", text: "Uploading...", pulse: false };
      if (file.uploaded)
        return { bg: "bg-green-500", text: "Uploaded", pulse: false };
      if (file.error)
        return { bg: "bg-red-500", text: "Upload Failed", pulse: false };
    }
    return { bg: "bg-gray-400", text: "No File", pulse: false };
  }, [files, isExecuting, executionSuccess, executionError]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // --- RENDER ---
  return (
    <div
      className={`relative group bg-blue-50 border-2 rounded-lg p-4 min-w-[300px] max-w-[320px] shadow-sm transition-all ${
        !validationStatus.isValid
          ? "border-red-500 shadow-red-200"
          : isExecuting
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : executionSuccess
              ? "border-green-500 shadow-md shadow-green-200"
              : executionError
                ? "border-red-500 shadow-md shadow-red-200"
                : selected
                  ? "border-blue-500 shadow-md"
                  : "border-blue-400 hover:border-gray-300"
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Execution Overlay Spinner */}
      {isExecuting && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-lg flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}

      {/* Success Overlay */}
      {executionSuccess && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {executionError && (
         <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      {/* Execute Button */}
      <div className="absolute -top-3 -right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            console.log("🚀 Execute button clicked for node:", id);
            console.log("📦 Node Data Keys:", Object.keys(data));
            console.log("📦 Node Data:", data);

            if (data.onExecuteNode) {
              console.log("✅ onExecuteNode function exists, calling it...");
              data.onExecuteNode(id);
            } else {
              console.error(
                "❌ onExecuteNode is MISSING in node data. This might be due to stale state. Please refresh the page.",
              );
            }
          }}
          className="p-1.5 bg-blue-500 text-white rounded-full shadow-md hover:bg-blue-600 transition-all"
          title="Execute to this node"
        >
          {isExecuting ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <PlayCircle className="w-3 h-3" />
          )}
        </button>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-green-500 border-2 border-white"
        style={{
          boxShadow:
            "0 0 0 4px rgba(47, 161, 238, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
        }}
      />
      <div className="flex items-start justify-between mb-4 cursor-move">
        <div className="flex items-center gap-2.5">
          <div
            className={`p-1.5 rounded shadow-sm transition-all ${
              isExecuting
                ? "bg-blue-100 animate-pulse"
                : executionSuccess
                  ? "bg-green-100"
                  : executionError
                    ? "bg-red-100"
                    : "bg-green-50"
            }`}
          >
            {isExecuting ? (
              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            ) : (
              <Upload
                className={`w-4 h-4 ${
                  executionSuccess
                    ? "text-green-700"
                    : executionError
                      ? "text-red-700"
                      : "text-green-600"
                }`}
              />
            )}
          </div>
          <div>
            <h3 className="font-medium text-gray-900 text-sm">
              On Document Upload
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg} ${
                  statusConfig.pulse ? "animate-pulse" : ""
                }`}
              />
              <p className="text-xs text-gray-500">{statusConfig.text}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NodeDocsButton nodeType="FILE-UPLOAD" />
          <button
            onClick={handleDeleteClick}
            className="text-red-400 hover:text-gray-600 p-1 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!validationStatus.isValid && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-red-900 mb-1">
                Configuration Required
              </p>
              <ul className="text-red-700 space-y-0.5 list-disc list-inside">
                {validationStatus.errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div onClick={stopPropagation}>
        {files.length === 0 ? (
          <div
            className={`text-center transition-all rounded-lg ${
              isDragging ? "bg-green-50" : "bg-gray-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.tiff,.doc,.docx"
            />
            <div
              className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center gap-2 ${
                isDragging ? "border-green-400" : "border-gray-300"
              }`}
            >
              <Upload className="w-6 h-6 text-gray-400" />
              <p className="text-xs text-gray-600 font-medium">
                {isDragging ? "Drop file to upload" : "Drag & drop file here"}
              </p>
              <p className="text-xs text-gray-400">or</p>
              <button
                onClick={handleBrowseClick}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Browse File
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {files.map((file, index) => (
              <div key={index}>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-white border border-gray-200 rounded flex items-center justify-center">
                      {file.type.includes("pdf") ? (
                        <FileText className="w-4 h-4 text-red-500" />
                      ) : (
                        <File className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-xs font-medium text-gray-800 truncate"
                      title={file.name}
                    >
                      {file.name}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    {file.uploading && (
                      <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                    )}
                    {file.uploaded && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                    {file.error && (
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                </div>
                {file.uploading && (
                  <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 w-full animate-pulse" />
                  </div>
                )}
                {file.error && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded-md">
                    {file.error}
                  </div>
                )}
              </div>
            ))}
            {files[0]?.uploaded && files[0]?.uploadData && (
              <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
                <button
                  onClick={handleClearFile}
                  className="w-full mt-2 px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear and Upload New
                </button>
              </div>
            )}
            {(files[0]?.error || files[0]?.uploading) && (
              <button
                onClick={handleClearFile}
                className="w-full mt-2 text-xs text-gray-500 hover:text-red-600 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentUploadNode;
